/**
 * What stands in each of a row's five cells.
 *
 * Five small components rather than five blocks inside the table, because three of them are drawn
 * TWICE — the still, the muscle pills and the equipment tiles all appear again in the preview panel
 * and the theatre — and because each owns a fallback the row around it should not have to know
 * about: a movement with no photograph, a piece of equipment whose picture 404s, a muscle list
 * longer than the column.
 *
 * None of them takes a DTO. `model/exerciseRow` is the seam, and a component that reached past it
 * would be a second copy of the projection.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarbellGlyph } from '~/shared/ui/ExerciseGlyphs';
import { ExerciseTypeGlyph } from '~/entities/exercise-library';
import { exerciseTypeColour } from '~/entities/exercise-library';
import photoBox from '~/shared/ui/photoBox.module.css';

import {
  EQUIPMENT_THUMBS,
  muscleOverflow,
  type EquipmentThumb,
  type ExerciseRow,
  type MusclePill,
} from '../model/exerciseRow';

import hidden from '~/shared/ui/visuallyHidden.module.css';
import still from './exerciseStill.module.css';
import tagParts from './tagParts.module.css';
import styles from './ExerciseTable.module.css';

/**
 * The movement's photograph, or the drawing that stands in its place.
 *
 * **One frame at four sizes.** The table's 44 × 33 cell, the preview panel's 16:9 card and the
 * theatre's hero and similar cards are one treatment with four geometries, so the treatment is
 * `exerciseStill.module.css` and the geometry arrives as the caller's own class. Three copies of
 * this component existed before the theatre landed, each with its own `useState` and its own
 * fallback; the theatre would have been the fourth.
 *
 * `useState` rather than a branch on the url: a still the catalogue NAMES and the media root does
 * not hold is not nothing on screen — Chrome draws its broken-file mark, which is worse than the
 * drawing this replaces. Ten of the named stills 404 locally. Keyed by the row at every call site,
 * so the flag cannot carry from one movement to the next.
 */
export function ExerciseStill({
  row,
  className,
  strokeWidth = 1.6,
  eager = false,
}: {
  readonly row: ExerciseRow;
  /** The caller's own box: its width, its height and its corner. */
  readonly className: string;
  /** Optical compensation for the drawn size, exactly as `ExerciseGlyphs` takes it. */
  readonly strokeWidth?: number;
  /** A still the trainer is already looking at is not below the fold. */
  readonly eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);

  return (
    <span className={`${String(still.frame)} ${className}`}>
      {row.stillUrl === null || failed ? (
        <BarbellGlyph className={still.glyph} strokeWidth={strokeWidth} />
      ) : (
        <img
          className={`${String(photoBox.photo)} ${String(still.photo)}`}
          src={row.stillUrl}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onError={() => {
            setFailed(true);
          }}
        />
      )}
    </span>
  );
}

/**
 * The type's glyph on its own wash square — the ONE colour in a row, and the only place on this
 * screen where a hue means anything at all.
 *
 * The glyph is `aria-hidden` and the word travels beside it, because a drawing cannot be read: a
 * listener hears the type once, as text.
 */
export function ExerciseTypeMark({ row }: { readonly row: ExerciseRow }) {
  return (
    <span className={styles.typeSquare} style={exerciseTypeColour(row.typeKey)}>
      <ExerciseTypeGlyph type={row.typeKey} className={styles.typeGlyph} />
      <span className={hidden.srOnly}>{row.typeLabel}</span>
    </span>
  );
}

/**
 * The muscles, capped by what the layout can fit.
 *
 * `cap` is two with the preview panel open and three without — a property of the layout, which is
 * why it arrives here rather than being baked into the projection. The `+N` carries the names it
 * stands for, in its title AND in its accessible text: a bare figure beside two words is a number a
 * trainer cannot act on.
 *
 * ── PRIMARY and SECONDARY are told apart by FILL, which a listener cannot hear ───────────────
 * The two pills differ by their ground and by nothing else, so a screen reader read three muscle
 * names with no way to know which of them the movement actually trains — the one thing the column
 * exists to say. Each pill carries the distinction as sr-only text, in the phone's own two words,
 * which is the pair the theatre's rail already prints as visible labels. Nothing changes on screen.
 */
export function ExerciseMuscles({
  muscles,
  cap,
}: {
  readonly muscles: readonly MusclePill[];
  readonly cap: number;
}) {
  const { t } = useTranslation();
  const overflow = muscleOverflow(muscles, cap);

  if (muscles.length === 0) {
    return <span className={styles.more}>{t('web:exerciseLibrary.row.noMuscles')}</span>;
  }

  const hiddenNames = overflow.hiddenNames.join(', ');
  return (
    <span className={styles.pills}>
      {overflow.shown.map((pill) => (
        <span
          key={pill.group}
          className={`${String(styles.pill)} ${pill.secondary ? String(styles.pillSecondary) : ''}`}
        >
          {pill.label}
          <span className={hidden.srOnly}>
            {' '}
            {t(
              pill.secondary
                ? 'exerciseLibrary.detail.musclesSecondary'
                : 'exerciseLibrary.detail.musclesPrimary',
            )}
          </span>
        </span>
      ))}
      {overflow.hidden > 0 && (
        <span
          className={styles.more}
          title={t('web:exerciseLibrary.row.moreMuscles', { names: hiddenNames })}
        >
          <span aria-hidden>+{overflow.hidden}</span>
          <span className={hidden.srOnly}>
            {t('web:exerciseLibrary.row.moreMuscles', { names: hiddenNames })}
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * One piece of equipment's disc: its photograph, or its initial when there is none.
 *
 * A component rather than a branch, for the one `useState` — the same fallback, for the same
 * reason, that the still above carries.
 */
function EquipmentDisc({ piece }: { readonly piece: EquipmentThumb }) {
  const [failed, setFailed] = useState(false);

  return (
    <span className={styles.thumb} title={piece.name}>
      {piece.imageUrl === null || failed ? (
        <i className={styles.thumbLetter} aria-hidden>
          {piece.name.slice(0, 1).toUpperCase()}
        </i>
      ) : (
        <img
          className={`${String(photoBox.photo)} ${String(styles.thumbPhoto)}`}
          src={piece.imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => {
            setFailed(true);
          }}
        />
      )}
    </span>
  );
}

/**
 * Up to three overlapping discs and a `+N`.
 *
 * The discs are pictures and carry no text, so the whole list is spoken once beside them — a
 * listener otherwise hears a row whose equipment column is silent, which reads as a movement that
 * needs none. A movement that genuinely needs none says so.
 */
export function ExerciseEquipmentThumbs({ row }: { readonly row: ExerciseRow }) {
  const { t } = useTranslation();

  if (row.equipment.length === 0) {
    return <span className={styles.more}>{t('web:exerciseLibrary.row.noEquipment')}</span>;
  }

  // The cap is the ROW's, not the movement's — the preview panel draws the same list in full.
  const overflow = row.equipment.length - EQUIPMENT_THUMBS;
  return (
    <span className={styles.thumbs}>
      {row.equipment.slice(0, EQUIPMENT_THUMBS).map((piece) => (
        <EquipmentDisc key={piece.id} piece={piece} />
      ))}
      {overflow > 0 && (
        <span className={styles.thumbsMore} aria-hidden>
          +{overflow}
        </span>
      )}
      <span className={hidden.srOnly}>
        {t('web:exerciseLibrary.row.equipment', {
          names: row.equipment.map((piece) => piece.name).join(', '),
        })}
      </span>
    </span>
  );
}

/** How many of the trainer's tags a row draws before the rest become a `+N`. The canon's two. */
const TAGS_SHOWN = 2;

/**
 * „Tagi" — the trainer's own tags on the movement, two and a `+N` naming the rest. Empty where the
 * movement carries none, which is the canon's own reading of an untagged row.
 */
export function ExerciseTags({ tags }: { readonly tags: readonly string[] }) {
  const { t } = useTranslation();
  if (tags.length === 0) return null;
  const hiddenNames = tags.slice(TAGS_SHOWN).join(', ');
  return (
    <span className={styles.pills}>
      {tags.slice(0, TAGS_SHOWN).map((tag) => (
        <span key={tag} className={`${String(tagParts.tag)} ${String(tagParts.small)}`}>
          <span className={tagParts.text}>{tag}</span>
        </span>
      ))}
      {tags.length > TAGS_SHOWN && (
        <span className={styles.more} title={t('web:exerciseLibrary.tags.more', { names: hiddenNames })}>
          <span aria-hidden>+{tags.length - TAGS_SHOWN}</span>
          <span className={hidden.srOnly}>
            {t('web:exerciseLibrary.tags.more', { names: hiddenNames })}
          </span>
        </span>
      )}
    </span>
  );
}
